import React, { useState, useMemo, useCallback } from 'react';
import { Heart, Plus, Minus, Scale, ChevronDown, Check } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PLACEHOLDER_IMG, handleImgError } from '@/lib/imagePlaceholder';

const API = window.location.origin + '/api';

function generateWeightRanges(weightType, piecesEstimates) {
  const ranges = [];
  if (weightType === 'meat') {
    for (let start = 10; start <= 96; start += 4) {
      const end = start + 4;
      const key = `K${start}-K${end}`;
      ranges.push({ label: `K${start} - K${end} worth`, value: start, pieces: piecesEstimates?.[key] || '' });
    }
  } else if (weightType === 'produce') {
    for (let start = 5; start <= 40; start += 5) {
      const end = start + 5;
      ranges.push({ label: `K${start} - K${end} worth`, value: start, pieces: '' });
    }
  }
  return ranges;
}

export default function ProductRow({ product, wishlisted, onWishlistToggle }) {
  const { user } = useAuth();
  const { cartItems, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart } = useCart();
  const navigate = useNavigate();
  const [showWeightPicker, setShowWeightPicker] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [busy, setBusy] = useState(false);

  const isWeighted = product.is_weighted;
  const weightRanges = useMemo(
    () => isWeighted ? generateWeightRanges(product.weight_type, product.pieces_estimates) : [],
    [isWeighted, product.weight_type, product.pieces_estimates]
  );

  const cartEntry = useMemo(
    () => cartItems.find(c => c.product_id === product.id),
    [cartItems, product.id]
  );
  const inCart = !!cartEntry;

  const hasDiscount = product.discount_percent > 0;
  const finalPrice = hasDiscount ? product.price * (1 - product.discount_percent / 100) : product.price;

  const handleAdd = useCallback(async () => {
    if (!user || user === false) { navigate('/login'); return; }
    if (isWeighted && !selectedRange) {
      setShowWeightPicker(true);
      return;
    }
    setBusy(true);
    try {
      const payload = { product_id: product.id, quantity: 1 };
      if (isWeighted && selectedRange) {
        payload.weight_price = selectedRange.value;
        payload.weight_label = selectedRange.label;
      }
      await axios.post(`${API}/cart`, payload, { withCredentials: true });
      await fetchCart();
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm('Your cart has items from another store. Clear cart and add this item?')) {
          await clearCart();
          const payload = { product_id: product.id, quantity: 1 };
          if (isWeighted && selectedRange) {
            payload.weight_price = selectedRange.value;
            payload.weight_label = selectedRange.label;
          }
          await axios.post(`${API}/cart`, payload, { withCredentials: true });
          await fetchCart();
        }
      } else {
        console.error('Failed to add to cart:', err.message);
      }
    }
    setShowWeightPicker(false);
    setBusy(false);
  }, [user, navigate, isWeighted, selectedRange, product.id, fetchCart, clearCart]);

  const handleIncrement = useCallback(async () => {
    if (!cartEntry) return;
    setBusy(true);
    try { await updateQuantity(product.id, cartEntry.quantity + 1); } finally { setBusy(false); }
  }, [cartEntry, updateQuantity, product.id]);

  const handleDecrement = useCallback(async () => {
    if (!cartEntry) return;
    setBusy(true);
    try {
      if (cartEntry.quantity <= 1) {
        await removeFromCart(product.id);
      } else {
        await updateQuantity(product.id, cartEntry.quantity - 1);
      }
    } finally { setBusy(false); }
  }, [cartEntry, updateQuantity, removeFromCart, product.id]);

  return (
    <div className={`product-row ${inCart ? 'product-row-selected' : ''}`} data-testid={`product-row-${product.id}`}>
      <div className="product-row-img">
        <img
          src={product.image_url || PLACEHOLDER_IMG}
          alt={product.name}
          loading="lazy"
          onError={handleImgError}
        />
        {inCart && (
          <span className="product-row-check" data-testid={`row-check-${product.id}`}>
            <Check size={12} />
          </span>
        )}
      </div>

      <div className="product-row-info">
        <div className="product-row-name" data-testid={`row-name-${product.id}`}>
          {product.name}
          {isWeighted && <Scale size={12} className="product-row-weight-icon" />}
          {hasDiscount && <span className="product-row-discount-badge">-{Math.round(product.discount_percent)}%</span>}
        </div>
        <div className="product-row-price-line">
          {isWeighted ? (
            <span className="product-row-price">
              {product.price_per_kg?.toFixed(2) || finalPrice.toFixed(2)} PGK
              <span className="product-row-unit"> /kg</span>
            </span>
          ) : (
            <>
              <span className="product-row-price">{finalPrice.toFixed(2)} PGK</span>
              {hasDiscount && <span className="product-row-orig-price">{product.price.toFixed(2)}</span>}
            </>
          )}
        </div>

        {isWeighted && (
          <div className="product-row-weight-picker">
            <button
              type="button"
              className="product-row-weight-btn"
              onClick={() => setShowWeightPicker(!showWeightPicker)}
              data-testid={`row-weight-toggle-${product.id}`}
            >
              <Scale size={12} />
              <span>{selectedRange ? selectedRange.label : 'Select amount'}</span>
              {selectedRange?.pieces && <span className="product-row-pieces-hint">{selectedRange.pieces}</span>}
              <ChevronDown size={12} />
            </button>
            {showWeightPicker && (
              <div className="product-row-weight-dropdown" data-testid={`row-weight-dropdown-${product.id}`}>
                {weightRanges.map(r => (
                  <button
                    key={r.value}
                    type="button"
                    className={`product-row-weight-opt ${selectedRange?.value === r.value ? 'active' : ''}`}
                    onClick={() => { setSelectedRange(r); setShowWeightPicker(false); }}
                    data-testid={`row-weight-opt-${product.id}-${r.value}`}
                  >
                    <span>{r.label}</span>
                    {r.pieces && <span className="product-row-pieces-badge">{r.pieces}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="product-row-actions">
        <button
          className={`product-row-wishlist ${wishlisted ? 'active' : ''}`}
          onClick={() => onWishlistToggle?.(product.id)}
          data-testid={`row-wishlist-${product.id}`}
          aria-label="Toggle wishlist"
        >
          <Heart size={16} fill={wishlisted ? 'currentColor' : 'none'} />
        </button>

        {inCart && !isWeighted ? (
          <div className="product-row-qty" data-testid={`row-qty-${product.id}`}>
            <button
              className="product-row-qty-btn"
              onClick={handleDecrement}
              disabled={busy}
              data-testid={`row-qty-minus-${product.id}`}
              aria-label="Decrease quantity"
            >
              <Minus size={14} />
            </button>
            <span className="product-row-qty-value" data-testid={`row-qty-value-${product.id}`}>
              {cartEntry.quantity}
            </span>
            <button
              className="product-row-qty-btn"
              onClick={handleIncrement}
              disabled={busy}
              data-testid={`row-qty-plus-${product.id}`}
              aria-label="Increase quantity"
            >
              <Plus size={14} />
            </button>
          </div>
        ) : inCart && isWeighted ? (
          <button
            className="product-row-add-btn product-row-added"
            onClick={() => removeFromCart(product.id)}
            data-testid={`row-remove-${product.id}`}
          >
            <Check size={14} /> Added
          </button>
        ) : (
          <button
            className="product-row-add-btn"
            onClick={handleAdd}
            disabled={busy}
            data-testid={`row-add-${product.id}`}
          >
            <Plus size={14} /> Add
          </button>
        )}
      </div>
    </div>
  );
}
